import type {
  V1Actor,
  V1Affix,
  V1BossLoot,
  V1Content,
  V1Enemy,
  V1Equipment,
  V1EquipmentSet,
  V1Item,
  V1Level,
  V1Manifest,
  V1Map,
  V1MapCell,
  V1MapSpecial,
  V1QualityProfile,
  V1Rules,
  V1Shop,
  V1Skill,
  V1State,
  V1Troop,
  V1Stats,
} from "./types";
import { V1_HEADERS } from "./headers";

const ROOT = "/game-data/v1";

const FILES = [
  "data-dictionary.tsv",
  "game-rules.tsv",
  "actors.tsv",
  "levels.tsv",
  "skills.tsv",
  "states.tsv",
  "enemies.tsv",
  "troops.tsv",
  "items.tsv",
  "equipment.tsv",
  "equipment-affixes.tsv",
  "quality-profiles.tsv",
  "equipment-sets.tsv",
  "boss-loot.tsv",
  "maps.tsv",
  "shops.tsv",
] as const;

type Row = Record<string, string>;

const splitList = (value: string): string[] => value === "-" ? [] : value.split(",");
const integer = (value: string, label: string, minimum = 0): number => {
  if (!/^-?\d+$/.test(value)) throw new Error(`${label} 不是整数：${value}`);
  const result = Number(value);
  if (!Number.isSafeInteger(result) || result < minimum) throw new Error(`${label} 超出范围：${value}`);
  return result;
};
const number = (value: string, label: string): number => {
  const result = Number(value);
  if (!Number.isFinite(result)) throw new Error(`${label} 不是有限数字：${value}`);
  return result;
};
const optionalInteger = (value: string, label: string): number | null =>
  value === "-" ? null : integer(value, label);
const optionalNumber = (value: string, label: string): number | null =>
  value === "-" ? null : number(value, label);
const boolean = (value: string, label: string): boolean => {
  if (value !== "0" && value !== "1") throw new Error(`${label} 必须是 0 或 1`);
  return value === "1";
};
const fields = (value: string, count: number, label: string): string[] => {
  const result = value.split("|");
  if (result.length !== count) throw new Error(`${label} 需要 ${count} 段，实际 ${result.length}`);
  return result;
};
const parseTraits = (value: string, label: string) =>
  splitList(value).map((entry, index) => {
    const [code, dataId, amount] = fields(entry, 3, `${label}[${index}]`);
    return {
      code: integer(code!, `${label}.code`, 1),
      dataId: integer(dataId!, `${label}.data_id`),
      value: number(amount!, `${label}.value`),
    };
  });
const parseLifeSteal = (value: string, label: string) =>
  splitList(value).map((entry, index) => {
    const [channel, mode, amount] = fields(entry, 3, `${label}[${index}]`);
    return {
      channel: integer(channel!, `${label}.channel`, 1),
      mode: integer(mode!, `${label}.mode`, 1),
      value: number(amount!, `${label}.value`),
    };
  });
const parseEffects = (value: string, label: string) =>
  splitList(value).map((entry, index) => {
    const values = entry.split("|");
    return {
      code: integer(values.shift() ?? "", `${label}[${index}].code`, 1),
      params: values.map((entryValue) => number(entryValue, `${label}[${index}].param`)),
    };
  });

const parseTsv = (source: string, file: string): Row[] => {
  const physicalLines = source.replace(/^\uFEFF/, "").split(/\r?\n/);
  const lines = physicalLines
    .map((line, index) => ({ line, physicalLine: index + 1 }))
    .filter(({ line }) => line.trim() !== "" && !line.startsWith("#"));
  if (lines.length < 2) throw new Error(`${file} 缺少数据行`);
  const header = lines[0]?.line.split("\t") ?? [];
  const expectedHeader = V1_HEADERS[file];
  if (!expectedHeader || header.join("\t") !== expectedHeader.join("\t")) {
    throw new Error(`${file} 表头与 jintu-data-v1 契约不一致`);
  }
  if (new Set(header).size !== header.length) throw new Error(`${file} 表头存在重复列`);
  return lines.slice(1).map(({ line, physicalLine }) => {
    const values = line.split("\t");
    if (values.length !== header.length) {
      throw new Error(`${file}:${physicalLine} 列数 ${values.length}/${header.length}`);
    }
    if (values.some((value) => value === "")) throw new Error(`${file}:${physicalLine} 不允许空单元格`);
    return Object.fromEntries(header.map((column, columnIndex) => [column, values[columnIndex] ?? "-"]));
  });
};

const readText = async (file: string, version?: string): Promise<string> => {
  const suffix = version ? `?v=${encodeURIComponent(version)}` : "";
  const response = await fetch(`${ROOT}/${file}${suffix}`, { cache: "no-store" });
  if (!response.ok) throw new Error(`V1 内容加载失败：${file}`);
  return response.text();
};
const readJson = async <T>(file: string, version?: string): Promise<T> => {
  const suffix = version ? `?v=${encodeURIComponent(version)}` : "";
  const response = await fetch(`${ROOT}/${file}${suffix}`, { cache: "no-store" });
  if (!response.ok) throw new Error(`V1 内容加载失败：${file}`);
  return response.json() as Promise<T>;
};

const stats = (row: Row, label: string): V1Stats => ({
  maxHp: integer(row.max_hp ?? "", `${label}.max_hp`, 0),
  maxMp: integer(row.max_mp ?? "", `${label}.max_mp`, 0),
  attack: integer(row.attack ?? "", `${label}.attack`, 0),
  defense: integer(row.defense ?? "", `${label}.defense`, 0),
  magicAttack: integer(row.magic_attack ?? "", `${label}.magic_attack`, 0),
  magicDefense: integer(row.magic_defense ?? "", `${label}.magic_defense`, 0),
  agility: integer(row.agility ?? "", `${label}.agility`, 0),
  luck: integer(row.luck ?? "", `${label}.luck`, 0),
});

const parseRules = (row: Row): V1Rules => ({
  gameName: row.game_name ?? "烬途",
  maxLevel: integer(row.max_level ?? "", "game-rules.max_level", 1),
  equipmentSlots: splitList(row.equipment_slots ?? "-").map((entry) =>
    integer(entry, "game-rules.equipment_slots", 1)
  ),
  startMapId: integer(row.start_map_id ?? "", "game-rules.start_map_id", 1),
  startEntryId: integer(row.start_entry_id ?? "", "game-rules.start_entry_id", 1),
  deathMapId: integer(row.death_map_id ?? "", "game-rules.death_map_id", 1),
  deathEntryId: integer(row.death_entry_id ?? "", "game-rules.death_entry_id", 1),
  initialGold: integer(row.initial_gold ?? "", "game-rules.initial_gold"),
  initialItems: splitList(row.initial_items ?? "-").map((entry, index) => {
    const [itemId, quantity] = fields(entry, 2, `game-rules.initial_items[${index}]`);
    return { itemId: integer(itemId!, "initial item id", 1), quantity: integer(quantity!, "initial quantity", 1) };
  }),
  levelUpRecoverAll: boolean(row.level_up_recover_all ?? "", "game-rules.level_up_recover_all"),
  itemStackLimit: integer(row.item_stack_limit ?? "", "game-rules.item_stack_limit", 1),
  weaponBagLimit: integer(row.weapon_bag_limit ?? "", "game-rules.weapon_bag_limit", 1),
  armorBagLimit: integer(row.armor_bag_limit ?? "", "game-rules.armor_bag_limit", 1),
  basicAttackSkillId: integer(row.basic_attack_skill_id ?? "", "game-rules.basic_attack_skill_id", 1),
  maximumDamage: integer(row.max_damage ?? "", "game-rules.max_damage"),
  maximumHealing: integer(row.max_healing ?? "", "game-rules.max_healing"),
  minimumDamage: integer(row.minimum_damage ?? "", "game-rules.minimum_damage"),
  criticalFlatBonus: number(row.critical_flat_bonus ?? "", "game-rules.critical_flat_bonus"),
  criticalDamagePercent: number(row.critical_damage_percent ?? "", "game-rules.critical_damage_percent"),
  dropFormulaCode: integer(row.drop_formula_code ?? "", "game-rules.drop_formula_code", 1),
  defaultSellPercent: number(row.default_sell_percent ?? "", "game-rules.default_sell_percent"),
  normalSpawnMinDistance: integer(row.normal_spawn_min_distance ?? "", "game-rules.normal_spawn_min_distance"),
  autoBattleEnabled: boolean(row.auto_battle_enabled ?? "", "game-rules.auto_battle_enabled"),
  autoBattleDefault: boolean(row.auto_battle_default ?? "", "game-rules.auto_battle_default"),
  autoBattlePersist: boolean(row.auto_battle_persist ?? "", "game-rules.auto_battle_persist"),
  legendaryPerkChancePercent: number(
    row.legendary_perk_chance_percent ?? "",
    "game-rules.legendary_perk_chance_percent",
  ),
  mythicPerkChancePercent: number(
    row.mythic_perk_chance_percent ?? "",
    "game-rules.mythic_perk_chance_percent",
  ),
});

const parseLevels = (rows: Row[]): V1Level[] => rows.map((row, index) => ({
  actorId: integer(row.actor_id ?? "", `levels:${index + 2}.actor_id`, 1),
  level: integer(row.level ?? "", `levels:${index + 2}.level`, 1),
  expRequired: integer(row.exp_required ?? "", `levels:${index + 2}.exp_required`),
  ...stats(row, `levels:${index + 2}`),
  learnSkills: splitList(row.learn_skills ?? "-").map((entry) => {
    const [skillId, skillLevel] = fields(entry, 2, `levels:${index + 2}.learn_skills`);
    return { skillId: integer(skillId!, "learn skill id", 1), skillLevel: integer(skillLevel!, "learn skill level", 1) };
  }),
}));

const parseSkills = (rows: Row[]): V1Skill[] => rows.map((row, index) => ({
  id: integer(row.skill_id ?? "", `skills:${index + 2}.skill_id`, 1),
  name: row.skill_name ?? "",
  type: integer(row.skill_type ?? "", `skills:${index + 2}.skill_type`, 1),
  iconSheet: integer(row.icon_sheet ?? "", `skills:${index + 2}.icon_sheet`, 1),
  iconIndex: integer(row.icon_index ?? "", `skills:${index + 2}.icon_index`),
  animationId: integer(row.animation_id ?? "", `skills:${index + 2}.animation_id`),
  scopeId: integer(row.scope_id ?? "", `skills:${index + 2}.scope_id`),
  occasionId: integer(row.occasion_id ?? "", `skills:${index + 2}.occasion_id`),
  hitTypeId: integer(row.hit_type_id ?? "", `skills:${index + 2}.hit_type_id`),
  damageTypeId: integer(row.damage_type_id ?? "", `skills:${index + 2}.damage_type_id`),
  elementId: integer(row.element_id ?? "", `skills:${index + 2}.element_id`),
  formulaCode: integer(row.formula_code ?? "", `skills:${index + 2}.formula_code`),
  repeats: integer(row.repeats ?? "", `skills:${index + 2}.repeats`),
  speed: integer(row.speed ?? "", `skills:${index + 2}.speed`),
  successRatePercent: number(row.success_rate_percent ?? "", `skills:${index + 2}.success_rate_percent`),
  cooldownTurns: integer(row.cooldown_turns ?? "", `skills:${index + 2}.cooldown_turns`),
  costType: integer(row.cost_type ?? "", `skills:${index + 2}.cost_type`),
  tpGain: integer(row.tp_gain ?? "", `skills:${index + 2}.tp_gain`),
  critical: boolean(row.critical ?? "", `skills:${index + 2}.critical`),
  criticalRatePercent: optionalNumber(row.critical_rate_percent ?? "-", `skills:${index + 2}.critical_rate_percent`),
  variancePercent: number(row.variance_percent ?? "", `skills:${index + 2}.variance_percent`),
  requiredWeaponTypes: splitList(row.required_weapon_types ?? "-").map((value) => integer(value, "required weapon type")),
  effects: parseEffects(row.effects ?? "-", `skills:${index + 2}.effects`),
  traits: parseTraits(row.traits ?? "-", `skills:${index + 2}.traits`),
  passiveStates: splitList(row.passive_states ?? "-").map((value) => integer(value, "skill passive state", 1)),
  ...stats(row, `skills:${index + 2}`),
  costs: Array.from({ length: 10 }, (_, level) => integer(row[`cost_${level}`] ?? "", `skills:${index + 2}.cost_${level}`)),
  powers: Array.from({ length: 10 }, (_, level) => number(row[`power_${level}`] ?? "", `skills:${index + 2}.power_${level}`)),
  extraType: integer(row.extra_type ?? "", `skills:${index + 2}.extra_type`),
  extras: Array.from({ length: 10 }, (_, level) => number(row[`extra_${level}`] ?? "", `skills:${index + 2}.extra_${level}`)),
  description: row.description ?? "",
}));

const parseEnemies = (rows: Row[]): V1Enemy[] => rows.map((row, index) => ({
  id: integer(row.enemy_id ?? "", `enemies:${index + 2}.enemy_id`, 1),
  name: row.enemy_name ?? "",
  rank: integer(row.enemy_rank ?? "", `enemies:${index + 2}.enemy_rank`, 1) as 1 | 2 | 3,
  spriteSheet: integer(row.sprite_sheet ?? "", `enemies:${index + 2}.sprite_sheet`, 1),
  spriteIndex: integer(row.sprite_index ?? "", `enemies:${index + 2}.sprite_index`),
  ...stats(row, `enemies:${index + 2}`),
  exp: integer(row.exp ?? "", `enemies:${index + 2}.exp`),
  gold: integer(row.gold ?? "", `enemies:${index + 2}.gold`),
  actions: splitList(row.actions ?? "-").map((entry) => {
    const [skillId, conditionType, param1, param2, stateId, weight] = fields(entry, 6, `enemies:${index + 2}.actions`);
    return {
      skillId: integer(skillId!, "enemy action skill", 1),
      conditionType: integer(conditionType!, "enemy action condition"),
      param1: number(param1!, "enemy action param1"),
      param2: number(param2!, "enemy action param2"),
      stateId: stateId === "-" ? null : integer(stateId!, "enemy action state", 1),
      weight: integer(weight!, "enemy action weight", 1),
    };
  }),
  traits: parseTraits(row.traits ?? "-", `enemies:${index + 2}.traits`),
  passiveStates: splitList(row.passive_states ?? "-").map((value) => integer(value, "enemy passive state", 1)),
  lifeSteal: parseLifeSteal(row.life_steal ?? "-", `enemies:${index + 2}.life_steal`),
  drops: splitList(row.drops ?? "-").map((entry) => {
    const [kind, entryId, chance] = fields(entry, 3, `enemies:${index + 2}.drops`);
    return {
      kind: integer(kind!, "drop kind", 1) as 1 | 2,
      entryId: integer(entryId!, "drop entry", 1),
      chancePercent: number(chance!, "drop chance"),
    };
  }),
}));

const parseTroops = (rows: Row[]): V1Troop[] => rows.map((row, index) => ({
  id: integer(row.troop_id ?? "", `troops:${index + 2}.troop_id`, 1),
  name: row.troop_name ?? "",
  canEscape: boolean(row.can_escape ?? "", `troops:${index + 2}.can_escape`),
  members: splitList(row.members ?? "-").map((entry) => {
    const [enemyId, positionId] = fields(entry, 2, `troops:${index + 2}.members`);
    return { enemyId: integer(enemyId!, "troop enemy", 1), positionId: integer(positionId!, "troop position", 1) };
  }),
}));

const parseItems = (rows: Row[]): V1Item[] => rows.map((row, index) => ({
  id: integer(row.item_id ?? "", `items:${index + 2}.item_id`, 1),
  name: row.item_name ?? "",
  type: integer(row.item_type ?? "", `items:${index + 2}.item_type`, 1),
  iconSheet: integer(row.icon_sheet ?? "", `items:${index + 2}.icon_sheet`, 1),
  iconIndex: integer(row.icon_index ?? "", `items:${index + 2}.icon_index`),
  animationId: integer(row.animation_id ?? "", `items:${index + 2}.animation_id`),
  description: row.description ?? "",
  maxStack: integer(row.max_stack ?? "", `items:${index + 2}.max_stack`),
  buyPrice: integer(row.buy_price ?? "", `items:${index + 2}.buy_price`),
  sellPrice: optionalInteger(row.sell_price ?? "-", `items:${index + 2}.sell_price`),
  usePlace: integer(row.use_place ?? "", `items:${index + 2}.use_place`),
  targetType: integer(row.target_type ?? "", `items:${index + 2}.target_type`),
  consumable: boolean(row.consumable ?? "", `items:${index + 2}.consumable`),
  successRatePercent: number(row.success_rate_percent ?? "", `items:${index + 2}.success_rate_percent`),
  requiredLevel: integer(row.required_level ?? "", `items:${index + 2}.required_level`, 1),
  effects: parseEffects(row.effects ?? "-", `items:${index + 2}.effects`),
}));

const parseEquipment = (rows: Row[]): V1Equipment[] => rows.map((row, index) => ({
  id: integer(row.equipment_id ?? "", `equipment:${index + 2}.equipment_id`, 1),
  name: row.equipment_name ?? "",
  equipmentType: integer(row.equipment_type ?? "", `equipment:${index + 2}.equipment_type`, 1),
  weaponTypeId: optionalInteger(row.weapon_type_id ?? "-", `equipment:${index + 2}.weapon_type_id`),
  armorTypeId: optionalInteger(row.armor_type_id ?? "-", `equipment:${index + 2}.armor_type_id`),
  qualityId: integer(row.quality_id ?? "", `equipment:${index + 2}.quality_id`, 1),
  iconSheet: integer(row.icon_sheet ?? "", `equipment:${index + 2}.icon_sheet`, 1),
  iconIndex: integer(row.icon_index ?? "", `equipment:${index + 2}.icon_index`),
  requiredLevel: integer(row.required_level ?? "", `equipment:${index + 2}.required_level`, 1),
  canRemove: boolean(row.can_remove ?? "", `equipment:${index + 2}.can_remove`),
  canSell: boolean(row.can_sell ?? "", `equipment:${index + 2}.can_sell`),
  ...stats(row, `equipment:${index + 2}`),
  buyPrice: integer(row.buy_price ?? "", `equipment:${index + 2}.buy_price`),
  sellPrice: optionalInteger(row.sell_price ?? "-", `equipment:${index + 2}.sell_price`),
  affixRules: splitList(row.affix_rules ?? "-").map((entry) => {
    const values = entry.split("|");
    return {
      lineNo: integer(values.shift() ?? "", "affix rule line", 1),
      chancePercent: number(values.shift() ?? "", "affix rule chance"),
      candidates: values[0] === "-" ? [] : values.map((value) => {
        const candidate = value.split(":");
        if (candidate.length !== 2) throw new Error(`affix rule candidate 需要 2 段，实际 ${candidate.length}`);
        const [affixId, weight] = candidate;
        return {
          affixId: integer(affixId!, "affix rule id", 1),
          weight: number(weight!, "affix rule weight"),
        };
      }),
    };
  }),
  traits: parseTraits(row.traits ?? "-", `equipment:${index + 2}.traits`),
  passiveStates: splitList(row.passive_states ?? "-").map((entry) =>
    integer(entry, `equipment:${index + 2}.passive_states`, 1)
  ),
  lifeSteal: parseLifeSteal(row.life_steal ?? "-", `equipment:${index + 2}.life_steal`),
  description: row.description ?? "",
}));

const parseAffixes = (rows: Row[]): V1Affix[] => rows.map((row, index) => ({
  id: integer(row.affix_id ?? "", `affixes:${index + 2}.affix_id`, 1),
  name: row.affix_name ?? "",
  type: integer(row.affix_type ?? "", `affixes:${index + 2}.affix_type`, 1),
  qualityId: optionalInteger(row.quality_id ?? "-", `affixes:${index + 2}.quality_id`),
  priceDelta: integer(row.price_delta ?? "", `affixes:${index + 2}.price_delta`),
  ...stats(row, `affixes:${index + 2}`),
  randomStatCode: optionalInteger(row.random_stat_code ?? "-", `affixes:${index + 2}.random_stat_code`),
  randomMin: optionalInteger(row.random_min ?? "-", `affixes:${index + 2}.random_min`),
  randomMax: optionalInteger(row.random_max ?? "-", `affixes:${index + 2}.random_max`),
  randomPricePerPoint: optionalInteger(row.random_price_per_point ?? "-", `affixes:${index + 2}.random_price_per_point`),
  traits: parseTraits(row.traits ?? "-", `affixes:${index + 2}.traits`),
  passiveStates: splitList(row.passive_states ?? "-").map((entry) =>
    integer(entry, `affixes:${index + 2}.passive_states`, 1)
  ),
  lifeSteal: parseLifeSteal(row.life_steal ?? "-", `affixes:${index + 2}.life_steal`),
  grantedSkills: splitList(row.granted_skills ?? "-").map((entry) =>
    integer(entry, `affixes:${index + 2}.granted_skills`, 1)
  ),
  bossEnemyId: optionalInteger(row.boss_enemy_id ?? "-", `affixes:${index + 2}.boss_enemy_id`),
  procChancePercent: number(row.proc_chance_percent ?? "", `affixes:${index + 2}.proc_chance_percent`),
  procDamagePercent: number(row.proc_damage_percent ?? "", `affixes:${index + 2}.proc_damage_percent`),
  skillDamageBonusPercent: number(row.skill_damage_bonus_percent ?? "", `affixes:${index + 2}.skill_damage_bonus_percent`),
  specialPool: integer(row.special_pool ?? "", `affixes:${index + 2}.special_pool`),
  minimumQualityId: integer(
    row.minimum_quality_id ?? "",
    `affixes:${index + 2}.minimum_quality_id`,
    1,
  ),
  description: row.description ?? "",
}));

const parseQualityProfiles = (rows: Row[]): V1QualityProfile[] => rows.map((row, index) => {
  const label = `quality-profiles:${index + 2}`;
  const qualityChances = Array.from({ length: 6 }, (_, qualityIndex) =>
    number(row[`quality_${qualityIndex + 1}_percent`] ?? "", `${label}.quality_${qualityIndex + 1}_percent`)
  );
  const pointRanges = Object.fromEntries(Array.from({ length: 5 }, (_, rangeIndex) => {
    const qualityId = rangeIndex + 2;
    return [qualityId, {
      min: integer(row[`points_${qualityId}_min`] ?? "", `${label}.points_${qualityId}_min`, 1),
      max: integer(row[`points_${qualityId}_max`] ?? "", `${label}.points_${qualityId}_max`, 1),
    }];
  }));
  return {
    id: integer(row.profile_id ?? "", `${label}.profile_id`, 1),
    name: row.profile_name ?? "",
    qualityChances,
    pointRanges,
    exceptionalChanceMultiplier: number(
      row.exceptional_chance_multiplier ?? "",
      `${label}.exceptional_chance_multiplier`,
    ),
    description: row.description ?? "",
  };
});

const parseSpecial = (source: string, label: string): V1MapSpecial => {
  const values = source.split("|");
  const type = integer(values.shift() ?? "", `${label}.type`, 1);
  if (type === 1) {
    if (![1, 2, 5, 6].includes(values.length)) throw new Error(`${label} 传送参数数目错误`);
    const hasGate = values.length >= 5;
    const itemId = hasGate ? integer(values[2]!, `${label}.requiredItemId`) : 0;
    const quantity = hasGate ? integer(values[3]!, `${label}.quantity`) : 0;
    const consume = hasGate ? boolean(values[4]!, `${label}.consume`) : false;
    if (itemId === 0 && (quantity !== 0 || consume)) {
      throw new Error(`${label} 无物品门槛时数量和消耗必须为0`);
    }
    if (itemId > 0 && quantity < 1) throw new Error(`${label} 物品门槛数量必须为正`);
    return {
      type: 1,
      targetMapId: integer(values[0]!, `${label}.targetMapId`, 1),
      targetEntryId: values.length >= 2 ? integer(values[1]!, `${label}.targetEntryId`) : 0,
      requiredItemId: itemId || null,
      quantity,
      consume,
      goldCost: values.length === 6 ? integer(values[5]!, `${label}.goldCost`) : 0,
    };
  }
  if (type === 2) {
    if (values.length !== 1 && values.length !== 2) throw new Error(`${label} Boss 参数数目错误`);
    return {
      type: 2,
      troopId: integer(values[0]!, `${label}.troopId`, 1),
      respawnSeconds: values.length === 2 ? integer(values[1]!, `${label}.respawnSeconds`) : null,
    };
  }
  if (type === 3) return { type: 3, localNpcId: integer(values[0]!, `${label}.localNpcId`, 1) };
  if (type === 4) return { type: 4, shopId: integer(values[0]!, `${label}.shopId`, 1) };
  if (type === 5) return { type: 5, entryId: integer(values[0]!, `${label}.entryId`, 1) };
  throw new Error(`${label} 未知地图标记类型 ${type}`);
};

const parseCell = (source: string, label: string): V1MapCell => {
  const match = /^(\d+)(?:【([^】]+)】)?$/.exec(source);
  if (!match) throw new Error(`${label} 非法地图格：${source}`);
  return { terrain: integer(match[1]!, `${label}.terrain`), special: match[2] ? parseSpecial(match[2], label) : null };
};

const parseMaps = (rows: Row[]): V1Map[] => rows.map((row, index) => {
  const width = integer(row.width ?? "", `maps:${index + 2}.width`, 1);
  const height = integer(row.height ?? "", `maps:${index + 2}.height`, 1);
  const sourceRows = (row.matrix ?? "").split("/");
  if (sourceRows.length !== height) throw new Error(`maps:${index + 2} 高度不匹配`);
  const cells = sourceRows.map((sourceRow, y) => {
    const sourceCells = sourceRow.split(",");
    if (sourceCells.length !== width) throw new Error(`maps:${index + 2} 第 ${y} 行宽度不匹配`);
    return sourceCells.map((cell, x) => parseCell(cell, `maps:${index + 2}[${x},${y}]`));
  });
  return {
    id: integer(row.map_id ?? "", `maps:${index + 2}.map_id`, 1),
    name: row.map_name ?? "",
    width,
    height,
    tilesetId: integer(row.tileset_id ?? "", `maps:${index + 2}.tileset_id`, 1),
    cells,
    defaultEntryId: integer(row.default_entry_id ?? "", `maps:${index + 2}.default_entry_id`, 1),
    npcs: splitList(row.npc_defs ?? "-").map((entry) => {
      const [id, name, spriteSheet, spriteIndex, direction, dialogue] = fields(entry, 6, `maps:${index + 2}.npc_defs`);
      return {
        id: integer(id!, "npc id", 1),
        name: decodeURIComponent(name!),
        spriteSheet: integer(spriteSheet!, "npc sprite sheet", 1),
        spriteIndex: integer(spriteIndex!, "npc sprite index"),
        direction: integer(direction!, "npc direction", 2),
        dialogue: decodeURIComponent(dialogue!).split("\n"),
      };
    }),
    normalTroops: splitList(row.normal_troops ?? "-").map((entry) => {
      const [troopId, weight] = fields(entry, 2, `maps:${index + 2}.normal_troops`);
      return { troopId: integer(troopId!, "normal troop id", 1), weight: integer(weight!, "normal troop weight", 1) };
    }),
    normalMaxAlive: integer(row.normal_max_alive ?? "", `maps:${index + 2}.normal_max_alive`),
    normalRespawnSeconds: integer(row.normal_respawn_seconds ?? "", `maps:${index + 2}.normal_respawn_seconds`),
    bossRespawnSeconds: integer(row.boss_respawn_seconds ?? "", `maps:${index + 2}.boss_respawn_seconds`),
    bgmId: integer(row.bgm_id ?? "", `maps:${index + 2}.bgm_id`),
    normalQualityProfileId: integer(row.normal_quality_profile_id ?? "", `maps:${index + 2}.normal_quality_profile_id`, 1),
    bossQualityProfileId: integer(row.boss_quality_profile_id ?? "", `maps:${index + 2}.boss_quality_profile_id`, 1),
  };
});

const parseShops = (rows: Row[]): V1Shop[] => rows.map((row, index) => ({
  id: integer(row.shop_id ?? "", `shops:${index + 2}.shop_id`, 1),
  name: row.shop_name ?? "",
  buyEnabled: boolean(row.buy_enabled ?? "", `shops:${index + 2}.buy_enabled`),
  sellEnabled: boolean(row.sell_enabled ?? "", `shops:${index + 2}.sell_enabled`),
  buyRatePercent: number(row.buy_rate_percent ?? "", `shops:${index + 2}.buy_rate_percent`),
  sellRatePercent: number(row.sell_rate_percent ?? "", `shops:${index + 2}.sell_rate_percent`),
  sellEntryTypes: splitList(row.sell_entry_types ?? "-").map((value) => integer(value, "shop sell type", 1)),
  goods: splitList(row.goods ?? "-").map((entry) => {
    const [entryType, entryId, priceOverride] = fields(entry, 3, `shops:${index + 2}.goods`);
    return {
      entryType: integer(entryType!, "shop good type", 1) as 1 | 2,
      entryId: integer(entryId!, "shop good id", 1),
      priceOverride: priceOverride === "-" ? null : integer(priceOverride!, "shop price override"),
    };
  }),
  description: row.description ?? "",
}));

const uniqueIndex = <T>(values: T[], id: (value: T) => number, label: string): Map<number, T> => {
  const result = new Map<number, T>();
  for (const value of values) {
    const key = id(value);
    if (result.has(key)) throw new Error(`${label} ID 重复：${key}`);
    result.set(key, value);
  }
  return result;
};

export const validateV1Content = (content: V1Content): void => {
  if (content.manifest.contract !== "jintu-data-v1" || content.manifest.schemaVersion !== 3) {
    throw new Error("V1 manifest 契约不匹配");
  }
  if (content.maps.length !== 39) throw new Error(`V1 必须包含 39 张地图，实际 ${content.maps.length}`);
  if (content.enemies.length !== 53) throw new Error(`V1 必须包含 53 个怪物，实际 ${content.enemies.length}`);
  if (content.skills.length !== 30) throw new Error(`V1 必须包含 30 个技能，实际 ${content.skills.length}`);
  if (content.items.length !== 57) throw new Error(`V1 必须包含 57 个物品，实际 ${content.items.length}`);
  if (content.equipment.length !== 131) throw new Error(`V1 必须包含 131 个装备底板，实际 ${content.equipment.length}`);
  if (content.qualityProfiles.length !== 12) throw new Error(`游戏2 必须包含 12 个品质档案，实际 ${content.qualityProfiles.length}`);
  if (!content.indexes.skills.has(content.rules.basicAttackSkillId)) {
    throw new Error(`基础攻击技能不存在：${content.rules.basicAttackSkillId}`);
  }
  for (const [label, chance] of [
    ["传说光环", content.rules.legendaryPerkChancePercent],
    ["神话光环", content.rules.mythicPerkChancePercent],
  ] as const) {
    if (chance < 0 || chance > 100) throw new Error(`${label}概率必须在 0-100`);
  }
  if (!content.indexes.maps.has(content.rules.startMapId) || !content.indexes.maps.has(content.rules.deathMapId)) {
    throw new Error("开局或死亡地图不存在");
  }
  if (content.levels.length !== content.actor.maxLevel) {
    throw new Error(`人物等级必须覆盖 1-${content.actor.maxLevel}`);
  }
  for (let level = 1; level <= content.actor.maxLevel; level += 1) {
    const row = content.indexes.levels.get(level);
    if (!row || row.actorId !== content.actor.id) throw new Error(`人物等级缺失：${level}`);
    for (const learned of row.learnSkills) {
      if (!content.indexes.skills.has(learned.skillId)) throw new Error(`等级 ${level} 学习技能不存在`);
    }
  }
  const entryKeys = new Set<string>();
  for (const map of content.maps) {
    if (!content.indexes.qualityProfiles.has(map.normalQualityProfileId)) {
      throw new Error(`地图 ${map.id} 普通怪品质档案不存在：${map.normalQualityProfileId}`);
    }
    if (!content.indexes.qualityProfiles.has(map.bossQualityProfileId)) {
      throw new Error(`地图 ${map.id} Boss 品质档案不存在：${map.bossQualityProfileId}`);
    }
    const specials = map.cells.flatMap((row, y) => row.map((cell, x) => ({ cell, x, y })));
    const entries = specials.filter(({ cell }) => cell.special?.type === 5);
    for (const { cell } of entries) {
      const entryId = cell.special?.type === 5 ? cell.special.entryId : -1;
      const key = `${map.id}:${entryId}`;
      if (entryKeys.has(key)) throw new Error(`地图入口重复：${key}`);
      entryKeys.add(key);
    }
    if (!entryKeys.has(`${map.id}:${map.defaultEntryId}`)) throw new Error(`地图 ${map.id} 缺少默认入口`);
    const bossTroops = new Set(specials.filter(({ cell }) => cell.special?.type === 2).map(({ cell }) => cell.special?.type === 2 ? cell.special.troopId : -1));
    for (const spawn of map.normalTroops) {
      if (!content.indexes.troops.has(spawn.troopId)) throw new Error(`地图 ${map.id} 普通敌群不存在：${spawn.troopId}`);
      if (bossTroops.has(spawn.troopId)) throw new Error(`地图 ${map.id} Boss 敌群进入普通池`);
    }
    if ((map.normalTroops.length === 0) !== (map.normalMaxAlive === 0 && map.normalRespawnSeconds === 0)) {
      throw new Error(`地图 ${map.id} 普通刷新三字段不一致`);
    }
    const npcReferences = new Set<number>();
    let spawnableCells = 0;
    for (const { cell, x, y } of specials) {
      if (cell.terrain === 0 && !cell.special) spawnableCells += 1;
      if (cell.special && cell.terrain === 1) {
        throw new Error(`地图 ${map.id} 的固定对象位于不可走格 (${x},${y})`);
      }
      if (cell.special?.type === 1) {
        const entryId = cell.special.targetEntryId || content.indexes.maps.get(cell.special.targetMapId)?.defaultEntryId;
        if (!entryId || !entryKeys.has(`${cell.special.targetMapId}:${entryId}`)) {
          // Cross-map entries are checked after the first pass below.
        }
      }
      if (cell.special?.type === 2 && !content.indexes.troops.has(cell.special.troopId)) throw new Error(`地图 ${map.id} Boss 敌群不存在`);
      if (cell.special?.type === 3) npcReferences.add(cell.special.localNpcId);
      if (cell.special?.type === 4 && !content.indexes.shops.has(cell.special.shopId)) throw new Error(`地图 ${map.id} 商店不存在`);
    }
    const npcDefinitions = new Set(map.npcs.map((npc) => npc.id));
    if (
      npcReferences.size !== npcDefinitions.size ||
      [...npcReferences].some((id) => !npcDefinitions.has(id))
    ) {
      throw new Error(`地图 ${map.id} 的 NPC 定义与矩阵引用不一致`);
    }
    if (spawnableCells < map.normalMaxAlive) throw new Error(`地图 ${map.id} 普通怪候选格不足`);
  }
  for (const map of content.maps) {
    for (const cell of map.cells.flat()) {
      if (cell.special?.type !== 1) continue;
      const target = content.indexes.maps.get(cell.special.targetMapId);
      if (!target) throw new Error(`地图 ${map.id} 传送目标不存在：${cell.special.targetMapId}`);
      const entryId = cell.special.targetEntryId || target.defaultEntryId;
      if (!entryKeys.has(`${target.id}:${entryId}`)) throw new Error(`地图 ${map.id} 传送入口不存在：${target.id}:${entryId}`);
      if (cell.special.requiredItemId && !content.indexes.items.has(cell.special.requiredItemId)) throw new Error(`地图 ${map.id} 门禁物品不存在`);
    }
  }
  for (const troop of content.troops) {
    if (troop.members.length === 0) throw new Error(`敌群 ${troop.id} 不能为空`);
    for (const member of troop.members) if (!content.indexes.enemies.has(member.enemyId)) throw new Error(`敌群 ${troop.id} 怪物不存在`);
  }
  for (const enemy of content.enemies) {
    if (!enemy.actions.some((action) => action.conditionType === 0)) throw new Error(`怪物 ${enemy.id} 缺少兜底行动`);
    for (const action of enemy.actions) if (!content.indexes.skills.has(action.skillId)) throw new Error(`怪物 ${enemy.id} 技能不存在`);
    for (const drop of enemy.drops) {
      if (drop.kind === 1 && !content.indexes.items.has(drop.entryId)) throw new Error(`怪物 ${enemy.id} 掉落物品不存在`);
      if (drop.kind === 2 && !content.indexes.equipment.has(drop.entryId)) throw new Error(`怪物 ${enemy.id} 掉落装备不存在`);
    }
  }
  for (const equipment of content.equipment) {
    for (const rule of equipment.affixRules) {
      const totalWeight = rule.candidates.reduce((sum, candidate) => sum + candidate.weight, 0);
      if (Math.abs(totalWeight - rule.chancePercent) > 1e-8) {
        throw new Error(`装备 ${equipment.id} 词缀权重合计与行概率不一致`);
      }
      for (const candidate of rule.candidates) {
        if (!content.indexes.affixes.has(candidate.affixId)) throw new Error(`装备 ${equipment.id} 词缀不存在：${candidate.affixId}`);
        if (candidate.weight <= 0) throw new Error(`装备 ${equipment.id} 词缀权重必须为正`);
      }
    }
  }
  for (const affix of content.affixes) {
    for (const skillId of affix.grantedSkills) {
      if (!content.indexes.skills.has(skillId)) {
        throw new Error(`词缀 ${affix.id} 授予技能不存在：${skillId}`);
      }
    }
    if (affix.procChancePercent < 0 || affix.procChancePercent > 100) {
      throw new Error(`词缀 ${affix.id} 触发概率必须在 0-100 之间`);
    }
    if (affix.procDamagePercent < 0 || affix.skillDamageBonusPercent < 0) {
      throw new Error(`词缀 ${affix.id} 的追击或光环数值不得为负`);
    }
    const abilityKinds = Number(affix.procChancePercent > 0 || affix.procDamagePercent > 0)
      + Number(affix.skillDamageBonusPercent > 0);
    if (affix.specialPool === 2 && (
      affix.bossEnemyId === null
      || affix.grantedSkills.length > 0
      || abilityKinds !== 1
      || (affix.procChancePercent > 0) !== (affix.procDamagePercent > 0)
    )) {
      throw new Error(`Boss 专属能力词缀 ${affix.id} 必须且只能配置一种触发追击或技能增伤光环`);
    }
  }
  for (const profile of content.qualityProfiles) {
    const chanceTotal = profile.qualityChances.reduce((sum, value) => sum + value, 0);
    if (Math.abs(chanceTotal - 100) > 1e-8) throw new Error(`品质档案 ${profile.id} 概率合计不是 100`);
    if (profile.qualityChances.some((value) => value < 0)) throw new Error(`品质档案 ${profile.id} 存在负概率`);
    if (profile.exceptionalChanceMultiplier <= 0) throw new Error(`品质档案 ${profile.id} 极品倍率必须大于 0`);
    for (let qualityId = 2; qualityId <= 6; qualityId += 1) {
      const range = profile.pointRanges[qualityId];
      if (!range || range.min > range.max) throw new Error(`品质档案 ${profile.id} 的品质 ${qualityId} 点数区间错误`);
    }
  }
  for (const set of content.sets) {
    for (const equipmentId of set.memberEquipment) {
      if (!content.indexes.equipment.has(equipmentId)) throw new Error(`套装 ${set.id} 装备不存在：${equipmentId}`);
    }
    for (const effect of Object.values(set.bonuses).flat()) {
      const [code, targetId] = effect.split("|");
      if (code === "6" && !content.indexes.skills.has(Number(targetId))) {
        throw new Error(`套装 ${set.id} 授予技能不存在：${targetId}`);
      }
    }
  }
  const bossEnemyIds = new Set(content.enemies.filter((enemy) => enemy.rank === 3).map((enemy) => enemy.id));
  if (content.bossLoot.length !== bossEnemyIds.size) {
    throw new Error(`Boss 专属掉落必须覆盖全部 ${bossEnemyIds.size} 个 Boss`);
  }
  for (const loot of content.bossLoot) {
    if (!bossEnemyIds.has(loot.bossEnemyId)) throw new Error(`Boss 专属掉落引用非 Boss：${loot.bossEnemyId}`);
    if (loot.equipmentPool.length === 0 || loot.abilityAffixPool.length !== 3) {
      throw new Error(`Boss ${loot.bossEnemyId} 专属装备或能力池不足`);
    }
    for (const equipmentId of loot.equipmentPool) {
      if (!content.indexes.equipment.has(equipmentId)) throw new Error(`Boss ${loot.bossEnemyId} 专属装备不存在：${equipmentId}`);
    }
    const abilities = loot.abilityAffixPool.map((affixId) => {
      const affix = content.indexes.affixes.get(affixId);
      if (!affix || affix.specialPool !== 2 || affix.bossEnemyId !== loot.bossEnemyId) {
        throw new Error(`Boss ${loot.bossEnemyId} 专属能力词缀无效：${affixId}`);
      }
      return affix;
    });
    if (
      abilities.filter((affix) => affix.procChancePercent > 0).length !== 2
      || abilities.filter((affix) => affix.skillDamageBonusPercent > 0).length !== 1
    ) {
      throw new Error(`Boss ${loot.bossEnemyId} 必须配置两种触发追击和一种技能增伤光环`);
    }
  }
  for (const shop of content.shops) {
    for (const good of shop.goods) {
      const exists = good.entryType === 1
        ? content.indexes.items.has(good.entryId)
        : content.indexes.equipment.has(good.entryId);
      if (!exists) throw new Error(`商店 ${shop.id} 商品不存在：${good.entryType}|${good.entryId}`);
    }
  }
};

export const compileV1Content = (
  manifest: V1Manifest,
  sources: Record<string, string>,
): V1Content => {
  const tables = Object.fromEntries(
    FILES.map((file) => [file, parseTsv(sources[file] ?? "", file)]),
  );
  for (const file of FILES) {
    const expected = manifest.tables[file];
    const actual = tables[file]?.length;
    if (expected !== actual) throw new Error(`${file} 行数与 manifest 不一致：${actual}/${expected}`);
  }
  const dictionary = new Map<string, Map<number, string>>();
  for (const row of tables["data-dictionary.tsv"] ?? []) {
    const type = row.dict_type ?? "";
    const code = integer(row.code ?? "", `dictionary.${type}.code`);
    const bucket = dictionary.get(type) ?? new Map<number, string>();
    if (bucket.has(code)) throw new Error(`字典重复：${type}:${code}`);
    bucket.set(code, row.name ?? "");
    dictionary.set(type, bucket);
  }
  const rulesRows = tables["game-rules.tsv"] ?? [];
  const actorRows = tables["actors.tsv"] ?? [];
  if (rulesRows.length !== 1 || actorRows.length !== 1) throw new Error("V1 当前只允许一行规则和一名玩家人物");
  const rules = parseRules(rulesRows[0]!);
  const actorRow = actorRows[0]!;
  const actor: V1Actor = {
    id: integer(actorRow.actor_id ?? "", "actors.actor_id", 1),
    name: actorRow.actor_name ?? "",
    initialLevel: integer(actorRow.initial_level ?? "", "actors.initial_level", 1),
    maxLevel: integer(actorRow.max_level ?? "", "actors.max_level", 1),
    traits: parseTraits(actorRow.traits ?? "-", "actors.traits"),
    passiveStates: splitList(actorRow.passive_states ?? "-").map((value) => integer(value, "actor passive state", 1)),
    description: actorRow.description ?? "",
  };
  const levels = parseLevels(tables["levels.tsv"] ?? []);
  const skills = parseSkills(tables["skills.tsv"] ?? []);
  const states: V1State[] = (tables["states.tsv"] ?? []).map((row, index) => ({
    id: integer(row.state_id ?? "", "states.state_id", 1),
    name: row.state_name ?? "",
    traits: parseTraits(row.traits ?? "-", `states:${index + 2}.traits`),
    lifeSteal: parseLifeSteal(row.life_steal ?? "-", `states:${index + 2}.life_steal`),
    effects: parseEffects(row.effects ?? "-", `states:${index + 2}.effects`),
  }));
  const enemies = parseEnemies(tables["enemies.tsv"] ?? []);
  const troops = parseTroops(tables["troops.tsv"] ?? []);
  const items = parseItems(tables["items.tsv"] ?? []);
  const equipment = parseEquipment(tables["equipment.tsv"] ?? []);
  const affixes = parseAffixes(tables["equipment-affixes.tsv"] ?? []);
  const qualityProfiles = parseQualityProfiles(tables["quality-profiles.tsv"] ?? []);
  const sets: V1EquipmentSet[] = (tables["equipment-sets.tsv"] ?? []).map((row) => {
    const bonuses: Record<number, string[]> = {};
    for (let count = 2; count <= 8; count += 1) bonuses[count] = splitList(row[`bonus_${count}`] ?? "-");
    return {
      id: integer(row.set_id ?? "", "sets.set_id", 1),
      name: row.set_name ?? "",
      memberEquipment: splitList(row.member_equipment ?? "-").map((value) => integer(value, "set member", 1)),
      bonuses,
      description: row.description ?? "",
    };
  });
  const bossLoot: V1BossLoot[] = (tables["boss-loot.tsv"] ?? []).map((row, index) => ({
    bossEnemyId: integer(row.boss_enemy_id ?? "", `boss-loot:${index + 2}.boss_enemy_id`, 1),
    bossName: row.boss_name ?? "",
    dropChancePercent: number(
      row.drop_chance_percent ?? "",
      `boss-loot:${index + 2}.drop_chance_percent`,
    ),
    minimumQualityId: integer(
      row.minimum_quality_id ?? "",
      `boss-loot:${index + 2}.minimum_quality_id`,
      1,
    ),
    equipmentPool: splitList(row.equipment_pool ?? "-").map((entry) =>
      integer(entry, `boss-loot:${index + 2}.equipment_pool`, 1)
    ),
    abilityAffixPool: splitList(row.ability_affix_pool ?? "-").map((entry) =>
      integer(entry, `boss-loot:${index + 2}.ability_affix_pool`, 1)
    ),
    description: row.description ?? "",
  }));
  const maps = parseMaps(tables["maps.tsv"] ?? []);
  const shops = parseShops(tables["shops.tsv"] ?? []);
  const content: V1Content = {
    manifest,
    dictionary,
    rules,
    actor,
    levels,
    skills,
    states,
    enemies,
    troops,
    items,
    equipment,
    affixes,
    qualityProfiles,
    sets,
    bossLoot,
    maps,
    shops,
    indexes: {
      levels: uniqueIndex(levels, (entry) => entry.level, "等级"),
      skills: uniqueIndex(skills, (entry) => entry.id, "技能"),
      enemies: uniqueIndex(enemies, (entry) => entry.id, "怪物"),
      troops: uniqueIndex(troops, (entry) => entry.id, "敌群"),
      items: uniqueIndex(items, (entry) => entry.id, "物品"),
      equipment: uniqueIndex(equipment, (entry) => entry.id, "装备"),
      affixes: uniqueIndex(affixes, (entry) => entry.id, "词缀"),
      qualityProfiles: uniqueIndex(qualityProfiles, (entry) => entry.id, "品质档案"),
      bossLoot: uniqueIndex(bossLoot, (entry) => entry.bossEnemyId, "Boss 专属掉落"),
      maps: uniqueIndex(maps, (entry) => entry.id, "地图"),
      shops: uniqueIndex(shops, (entry) => entry.id, "商店"),
    },
  };
  validateV1Content(content);
  return content;
};

export const loadV1Content = async (): Promise<V1Content> => {
  const manifest = await readJson<V1Manifest>("manifest.json", String(Date.now()));
  const sourceValues = await Promise.all(
    FILES.map((file) => readText(file, manifest.contentHash)),
  );
  const sources = Object.fromEntries(
    FILES.map((file, index) => [file, sourceValues[index] ?? ""]),
  );
  await verifyV1Hashes(manifest, sources);
  return compileV1Content(manifest, sources);
};

const sha256 = async (source: string): Promise<string> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("当前运行环境不支持 V1 内容哈希校验");
  const digest = await subtle.digest("SHA-256", new TextEncoder().encode(source));
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const verifyV1Hashes = async (
  manifest: V1Manifest,
  sources: Record<string, string>,
): Promise<void> => {
  const hashes: Record<string, string> = {};
  await Promise.all(FILES.map(async (file) => {
    const actual = await sha256(sources[file] ?? "");
    const expected = manifest.hashes[file];
    if (actual !== expected) throw new Error(`${file} 内容哈希与 manifest 不一致`);
    hashes[file] = actual;
  }));
  const canonical = Object.entries(hashes)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([file, hash]) => `${file}:${hash}`)
    .join("\n");
  if (await sha256(canonical) !== manifest.contentHash) {
    throw new Error("V1 contentHash 与 15 张主表不一致");
  }
};
